import wootConstants from 'dashboard/constants/globals';

const { ASSIGNEE_TYPE, STATUS_TYPE, CONVERSATION_TYPE } = wootConstants;

export const ADMIN_ASSIGNEE_SCOPE = {
  ALL: 'all',
  MINE_AND_UNASSIGNED: 'mine_and_unassigned',
};

export const ADMIN_ASSIGNEE_SCOPE_VALUES = Object.values(ADMIN_ASSIGNEE_SCOPE);

/** Default ON: unassigned threads appear in Pendentes (unattended chip). */
export const normalizeIncludeUnassignedInPendentes = value => {
  if (value === false || value === 'false' || value === 0) return false;
  return true;
};

// SiteUp: catálogo de "filtros rápidos" da lista de conversas.
//
// Cada item é um preset que combina as três dimensões de filtragem
// (assignee_type + status + conversation_type) num único chip clicável.
// `defaultHidden: true` => não aparece na barra por padrão, mas fica disponível
// no modo "Personalizar" pra o usuário ativar. A ordem e a visibilidade são
// personalizáveis por usuário (uiSettings) — junto com as Pastas (custom views),
// que o ChatList injeta dinamicamente na lista.
//
// IMPORTANTE (corrigido na F1.2e): este arquivo NÃO é escaneado pelo Tailwind.
// Os globs de `content` cobrem `.vue` em todo o dashboard, mas de `.js` só
// `helper/`, `composables/`, `components-next/` e `routes/dashboard/` —
// `components/widgets/**/*.js` ficou de fora. O CSS dos ícones daqui só existe
// porque as mesmas classes aparecem literalmente em arquivo escaneado
// (`dashboard/helper/legacyIconClass.js` cobre as escolhíveis).
// SiteUp v6: segmento primário da lista (rótulos, não ícones).
// Semântica explícita: Não lidas → Pendentes → Minhas.
export const PRIMARY_SEGMENT_KEYS = ['unread', 'unattended', 'mine'];

// Ordem canônica forçada no personalizador — mesmos três do segmento.
export const CANONICAL_VISIBLE_FILTER_KEYS = [...PRIMARY_SEGMENT_KEYS];

// SiteUp Conversas chrome: atalhos builtin visíveis por padrão (todos os users).
// Builtin `kanban` is pinned only when the user has no kanban-* presets; see
// hideBuiltinKanbanWhenPresetsExist (avoids two square-kanban icons).
export const DEFAULT_VISIBLE_SHORTCUT_KEYS = [
  'all',
  'automation',
  'kanban',
  'scheduled',
];

// Built-ins ocultos da barra por padrão (Unassigned sai dos ícones pequenos).
export const DEFAULT_HIDDEN_BUILTIN_KEYS = [
  'open',
  'active',
  'resolved',
  'unassigned',
];

// Bump resets builtin visibility for existing users (preserves folders/customs).
// v5: separates the old Pendentes key from the real Não lidas filter.
export const SITEUP_BUILTIN_SHORTCUT_DEFAULTS_VERSION = 5;

// v4 used `unread` for Pendentes/UNATTENDED. Only configs explicitly written by
// that version are migrated; empty/new configs keep the canonical `unread` key.
const LEGACY_PENDING_SEMANTICS_VERSION = 4;

export const QUICK_FILTERS = [
  {
    // Key `unread`: real unread incoming messages.
    key: 'unread',
    icon: 'i-ph-envelope',
    i18nKey: 'UNREAD',
    assigneeType: ASSIGNEE_TYPE.ALL,
    // SiteUp (filtro GLOBAL): Não lidas cobre SEMPRE
    // aberto+pendente+adiado (todos os status menos resolvido).
    status: STATUS_TYPE.ACTIVE,
    conversationType: CONVERSATION_TYPE.UNREAD,
    // Meta all_count scoped by conversation_type=unread.
    countKey: 'allCount',
    defaultHidden: false,
  },
  {
    // `unattended` é o filtro Pendentes/aguardando resposta.
    key: 'unattended',
    icon: 'i-ph-clock',
    i18nKey: 'UNANSWERED',
    assigneeType: ASSIGNEE_TYPE.ALL,
    // Pendentes cobre aberto+pendente+adiado (todos os status menos resolvido).
    status: STATUS_TYPE.ACTIVE,
    conversationType: CONVERSATION_TYPE.UNATTENDED,
    // Meta all_count scoped by conversation_type=unattended.
    countKey: 'allCount',
    defaultHidden: false,
  },
  {
    key: 'unassigned',
    icon: 'i-ph-circle',
    i18nKey: 'UNASSIGNED',
    assigneeType: ASSIGNEE_TYPE.UNASSIGNED,
    // SiteUp (filtro GLOBAL): Não atribuídas cobre aberto+pendente+adiado
    // (todos menos resolvido), fixo — igual às demais. Sem seletor por chip.
    status: STATUS_TYPE.ACTIVE,
    conversationType: '',
    countKey: 'unAssignedCount',
    // SiteUp v3: sai dos ícones pequenos por padrão (disponível no personalizar).
    defaultHidden: true,
  },
  {
    key: 'mine',
    icon: 'i-ph-user',
    i18nKey: 'MINE',
    assigneeType: ASSIGNEE_TYPE.ME,
    // SiteUp (filtro GLOBAL, obrigatório): Minhas cobre SEMPRE
    // aberto+pendente+adiado (todos os status menos resolvido). Fixo pra todos
    // os usuários — sem seletor de status por chip (statusConfigurable removido).
    status: STATUS_TYPE.ACTIVE,
    conversationType: '',
    countKey: 'mineCount',
    defaultHidden: false,
  },
  {
    key: 'all',
    icon: 'i-ph-tray',
    i18nKey: 'ALL',
    // Admins: account-wide. Agents: frontend applies ME + status all (own history).
    assigneeType: ASSIGNEE_TYPE.ALL,
    status: STATUS_TYPE.ALL,
    conversationType: '',
    countKey: 'allCount',
    defaultHidden: false,
  },
  {
    key: 'open',
    icon: 'i-ph-radio-button',
    i18nKey: 'OPEN',
    assigneeType: ASSIGNEE_TYPE.ALL,
    status: STATUS_TYPE.OPEN,
    conversationType: '',
    // countKey é o campo do meta; a contagem é escopada pelo `status` do chip
    // (allCount@open = total de abertas). Antes era null e o 92 acabava
    // "vazando" pro chip Todas, que lê do mesmo triple status-scoped.
    countKey: 'allCount',
    defaultHidden: true,
  },
  {
    key: 'active',
    icon: 'i-ph-stack',
    i18nKey: 'ACTIVE',
    assigneeType: ASSIGNEE_TYPE.ALL,
    status: STATUS_TYPE.ACTIVE,
    conversationType: '',
    // allCount@active = total em andamento (aberto+pendente+adiado).
    countKey: 'allCount',
    defaultHidden: true,
  },
  {
    // SiteUp: faltava o filtro de Resolvidas nos padrões da lista.
    key: 'resolved',
    icon: 'i-ph-check-circle',
    i18nKey: 'RESOLVED',
    assigneeType: ASSIGNEE_TYPE.ALL,
    status: STATUS_TYPE.RESOLVED,
    conversationType: '',
    countKey: 'allCount',
    defaultHidden: true,
  },
  {
    key: 'scheduled',
    icon: 'i-ph-calendar-check',
    i18nKey: 'SCHEDULED',
    assigneeType: ASSIGNEE_TYPE.ALL,
    // SiteUp (filtro global): agendadas também cobre todos os status menos
    // resolvido — uma conversa pendente/adiada com mensagem agendada aparece.
    status: STATUS_TYPE.ACTIVE,
    conversationType: CONVERSATION_TYPE.SCHEDULED,
    // SiteUp: all_count do meta filtrado por scheduled (chave = status|scheduled).
    countKey: 'allCount',
    // SiteUp v3: atalho default na barra (com Automação + Kanban).
    defaultHidden: false,
  },
  {
    // SiteUp: automações em massa (campanha WA + comentário IG/FB). Inclui
    // threads resolved ainda sem reply — sai na 1ª mensagem de atendente humano.
    key: 'automation',
    icon: 'i-ph-lightning',
    i18nKey: 'AUTOMATION',
    assigneeType: ASSIGNEE_TYPE.ALL,
    status: STATUS_TYPE.ALL,
    conversationType: CONVERSATION_TYPE.AUTOMATION,
    countKey: 'allCount',
    // SiteUp: atalho default na barra.
    defaultHidden: false,
  },
  {
    key: 'kanban',
    icon: 'i-ph-kanban',
    i18nKey: 'KANBAN',
    assigneeType: 'kanban',
    // SiteUp v654: membership is server-side (kanban_funnel_id / kanban_stage).
    status: STATUS_TYPE.ACTIVE,
    conversationType: '',
    countKey: null,
    // SiteUp v3: atalho default na barra (funil/etapa sob a barra quando ativo).
    defaultHidden: false,
    requiresKanban: true,
  },
];

// SiteUp: ícones que o usuário pode escolher pra cada filtro (no modo
// Personalizar) — facilita localizar pastas/custom views pelo ícone.
// SiteUP V2 (F1.2e): catálogo em Phosphor (`i-ph-*`).
// Este catálogo é escaneado explicitamente pelo Tailwind para emitir todos os ícones.
// Escolhas antigas continuam preservadas pelo resolvedor na leitura.
export const FILTER_ICON_CHOICES = [
  'i-ph-folder',
  'i-ph-folder-open',
  'i-ph-tray',
  'i-ph-star',
  'i-ph-flag',
  'i-ph-bookmark',
  'i-ph-tag',
  'i-ph-heart',
  'i-ph-radio-button',
  'i-ph-check-circle',
  'i-ph-clock',
  'i-ph-calendar',
  'i-ph-bell',
  'i-ph-lightning',
  'i-ph-fire',
  'i-ph-target',
  'i-ph-trophy',
  'i-ph-currency-dollar',
  'i-ph-house',
  'i-ph-briefcase',
  'i-ph-users',
  'i-ph-chat-circle',
  'i-ph-phone',
  'i-ph-funnel',
  'i-ph-stack',
  'i-ph-push-pin',
  'i-ph-hash',
  'i-ph-archive',
];

// SiteUp: status atômicos que o usuário pode COMBINAR (multi-seleção) num chip
// configurável (Minhas / Não atribuídas). Marcar Aberto+Pendente+Adiado = mesmo
// que "ativas"; marcar os 4 = "tudo". A ordem aqui é a ordem CANÔNICA usada pra
// montar a string combinada de status (ex.: "open,pending").
export const FILTER_STATUS_CHOICES = [
  { value: STATUS_TYPE.OPEN, label: 'Aberto' },
  { value: STATUS_TYPE.PENDING, label: 'Pendente' },
  { value: STATUS_TYPE.SNOOZED, label: 'Adiado' },
  { value: STATUS_TYPE.RESOLVED, label: 'Resolvido' },
];

// Prefixo das chaves de Pasta (custom view) na barra de filtros rápidos.
export const FOLDER_FILTER_PREFIX = 'folder-';

// SiteUp v654: presets Kanban duplicáveis (persistidos em ui_settings, não
// localStorage). Built-in `kanban` stays in the catalog; when any kanban-*
// preset exists, the builtin is hidden so the icon bar shows one Kanban.
export const KANBAN_PRESET_PREFIX = 'kanban-';

export const isFolderFilterKey = key =>
  typeof key === 'string' && key.startsWith(FOLDER_FILTER_PREFIX);

export const folderIdFromKey = key =>
  isFolderFilterKey(key) ? key.slice(FOLDER_FILTER_PREFIX.length) : null;

// Pasta excluída não deixa a chave folder-<id> para trás em order, hidden,
// icons e activeKey. Devolve só o patch do que mudou, ou null se nada a limpar.
export const removeFolderShortcutPatch = (raw, folderId) => {
  const key = `${FOLDER_FILTER_PREFIX}${folderId}`;
  const cfg = raw && typeof raw === 'object' ? raw : {};
  const patch = {};
  ['order', 'hidden'].forEach(field => {
    if (Array.isArray(cfg[field]) && cfg[field].includes(key)) {
      patch[field] = cfg[field].filter(item => item !== key);
    }
  });
  if (cfg.icons && typeof cfg.icons === 'object' && key in cfg.icons) {
    patch.icons = { ...cfg.icons };
    delete patch.icons[key];
  }
  // mesmo resultado que o normalizador já dava para chave fora de order
  if (cfg.activeKey === key) patch.activeKey = 'unread';
  return Object.keys(patch).length ? patch : null;
};

// Troca de ícone de uma pasta existente: só gera patch quando muda de fato
export const folderIconPatch = (raw, folderId, icon) => {
  if (!folderId || typeof icon !== 'string' || !icon.startsWith('i-')) {
    return null;
  }
  const key = `${FOLDER_FILTER_PREFIX}${folderId}`;
  const cfg = raw && typeof raw === 'object' ? raw : {};
  const icons = cfg.icons && typeof cfg.icons === 'object' ? cfg.icons : {};
  if (icons[key] === icon) return null;
  return { icons: { ...icons, [key]: icon } };
};

export const isKanbanPresetKey = key =>
  typeof key === 'string' &&
  (key === 'kanban' || key.startsWith(KANBAN_PRESET_PREFIX));

export const isDuplicableKanbanPresetKey = key =>
  typeof key === 'string' && key.startsWith(KANBAN_PRESET_PREFIX);

/** Prefer user kanban-* presets over the single builtin `kanban` chip. */
export const hideBuiltinKanbanWhenPresetsExist = (hiddenSet, kanbanPresets) => {
  if ((kanbanPresets || []).length > 0) {
    hiddenSet.add('kanban');
  }
};

const newKanbanPresetKey = () =>
  `${KANBAN_PRESET_PREFIX}${Date.now().toString(36)}${Math.random()
    .toString(36)
    .slice(2, 8)}`;

export const normalizeKanbanPreset = (raw = {}) => {
  const key =
    typeof raw.key === 'string' && isDuplicableKanbanPresetKey(raw.key)
      ? raw.key
      : newKanbanPresetKey();
  const funnelId =
    raw.funnelId != null && String(raw.funnelId).trim() !== ''
      ? String(raw.funnelId)
      : '';
  let stageKey = typeof raw.stageKey === 'string' ? raw.stageKey : '';
  // Stage without funnel is invalid — drop stage fail-closed at normalize time.
  if (!funnelId) stageKey = '';
  return {
    key,
    name:
      typeof raw.name === 'string' && raw.name.trim()
        ? raw.name.trim()
        : 'Kanban',
    icon:
      typeof raw.icon === 'string' && raw.icon ? raw.icon : 'i-ph-kanban',
    funnelId,
    stageKey,
  };
};

export const createKanbanPreset = (overrides = {}) =>
  normalizeKanbanPreset({
    key: newKanbanPresetKey(),
    name: overrides.name || 'Kanban',
    icon: overrides.icon || 'i-ph-kanban',
    funnelId: overrides.funnelId || '',
    stageKey: overrides.stageKey || '',
  });

export const duplicateKanbanPreset = (source, overrides = {}) =>
  createKanbanPreset({
    name: overrides.name || `${source?.name || 'Kanban'} copy`,
    icon: overrides.icon || source?.icon,
    funnelId:
      overrides.funnelId !== undefined
        ? overrides.funnelId
        : source?.funnelId || '',
    stageKey:
      overrides.stageKey !== undefined
        ? overrides.stageKey
        : source?.stageKey || '',
  });

/**
 * One-shot builtin shortcut reset for existing users.
 * Keeps folders / kanbanPresets / custom visibility; only rewrites builtin
 * quick-filter defaults to Todas + Automation + Kanban + Scheduled.
 * When kanbanPresets exist, builtin `kanban` is hidden (one icon).
 */
export const migrateBuiltinShortcutDefaults = ({
  order = [],
  hidden = [],
  kanbanPresets = [],
} = {}) => {
  const nextOrder = order.map(String);
  DEFAULT_VISIBLE_SHORTCUT_KEYS.forEach(key => {
    if (!nextOrder.includes(key)) nextOrder.push(key);
  });
  const hiddenSet = new Set(hidden.map(String));
  DEFAULT_VISIBLE_SHORTCUT_KEYS.forEach(key => hiddenSet.delete(key));
  DEFAULT_HIDDEN_BUILTIN_KEYS.forEach(key => hiddenSet.add(key));
  hideBuiltinKanbanWhenPresetsExist(hiddenSet, kanbanPresets);
  return {
    order: nextOrder,
    hidden: [...hiddenSet],
    builtinDefaultsVersion: SITEUP_BUILTIN_SHORTCUT_DEFAULTS_VERSION,
  };
};

export const needsBuiltinShortcutDefaultsMigration = (raw = {}) =>
  Number(raw?.builtinDefaultsVersion) !==
  SITEUP_BUILTIN_SHORTCUT_DEFAULTS_VERSION;

/**
 * ui_settings key for conversation quick filters, scoped per account.
 * Legacy unscoped `conversations_quick_filters` remains a read-only seed.
 */
export const conversationsQuickFiltersStorageKey = accountId => {
  const id = Number(accountId);
  if (!id) return 'conversations_quick_filters';
  return `conversations_quick_filters_${id}`;
};

/**
 * Read raw quick-filter config for an account.
 * Prefers the account-scoped key; falls back to the legacy global key so
 * existing preferences still seed the first visit to that account.
 */
export const readConversationsQuickFiltersRaw = (uiSettings, accountId) => {
  const settings =
    uiSettings && typeof uiSettings === 'object' ? uiSettings : {};
  const key = conversationsQuickFiltersStorageKey(accountId);
  const scoped = settings[key];
  if (scoped && typeof scoped === 'object' && !Array.isArray(scoped)) {
    return scoped;
  }
  const legacy = settings.conversations_quick_filters;
  if (legacy && typeof legacy === 'object' && !Array.isArray(legacy)) {
    return legacy;
  }
  return {};
};

/**
 * Keep only inbox IDs that belong to the current account.
 * Cross-account leftovers (e.g. Jan's inboxes while viewing account 11)
 * otherwise produce an empty "Todas" list with a "N caixas" chip.
 */
export const sanitizeGlobalInboxIds = (globalInbox, validInboxIds) => {
  let raw = [];
  if (Array.isArray(globalInbox)) {
    raw = globalInbox;
  } else if (globalInbox) {
    raw = [globalInbox];
  }
  const ids = raw.map(Number).filter(Boolean);
  if (!Array.isArray(validInboxIds) || validInboxIds.length === 0) {
    return ids;
  }
  const allowed = new Set(validInboxIds.map(Number).filter(Boolean));
  return ids.filter(id => allowed.has(id));
};

/**
 * Non-destructive read/UI normalization for conversations_quick_filters.
 * Ensures the three primary segment keys are visible and ordered first while
 * preserving folders, kanban presets, icons, inbox/admin scope and a valid
 * activeKey. Never mutates the DB — callers persist only on user edits or
 * when needsBuiltinDefaultsPersist is true (one-shot builtin defaults bump).
 *
 * @param {object} raw
 * @param {{ validInboxIds?: number[] }} [options]
 */
export const normalizeConversationsQuickFilters = (raw = {}, options = {}) => {
  const cfg = raw && typeof raw === 'object' ? raw : {};
  const rawBuiltinDefaultsVersion = Number(cfg.builtinDefaultsVersion) || 0;
  const shouldMigrateLegacyPending =
    rawBuiltinDefaultsVersion === LEGACY_PENDING_SEMANTICS_VERSION;
  const normalizeLegacyPendingKey = key =>
    shouldMigrateLegacyPending && key === 'unread' ? 'unattended' : key;
  const rawOrder = Array.isArray(cfg.order)
    ? cfg.order.map(String).map(normalizeLegacyPendingKey)
    : [];
  const rawHidden = Array.isArray(cfg.hidden)
    ? cfg.hidden.map(String).map(normalizeLegacyPendingKey)
    : [];
  const kanbanPresets = (
    Array.isArray(cfg.kanbanPresets) ? cfg.kanbanPresets : []
  )
    .map(normalizeKanbanPreset)
    .filter(Boolean);
  const shouldMigrateBuiltins = needsBuiltinShortcutDefaultsMigration(cfg);

  const builtinKeys = QUICK_FILTERS.map(item => item.key);
  const knownKeys = new Set([
    ...builtinKeys,
    ...kanbanPresets.map(item => item.key),
    ...rawOrder.filter(
      key => isFolderFilterKey(key) || isDuplicableKanbanPresetKey(key)
    ),
  ]);

  // Start from user order, drop unknowns except folders / kanban presets.
  let order = rawOrder.filter(
    key => knownKeys.has(key) || isFolderFilterKey(key)
  );

  // Force the primary segment keys present and visible.
  CANONICAL_VISIBLE_FILTER_KEYS.forEach(key => {
    if (!order.includes(key)) order.push(key);
  });

  // Default shortcuts always present in order.
  DEFAULT_VISIBLE_SHORTCUT_KEYS.forEach(key => {
    if (!order.includes(key)) order.push(key);
  });

  // Ensure known kanban presets appear in the order list (after globals).
  kanbanPresets.forEach(preset => {
    if (!order.includes(preset.key)) order.push(preset.key);
  });

  // Place primary keys first and pin Todas as the first secondary icon.
  const canonicalSet = new Set(CANONICAL_VISIBLE_FILTER_KEYS);
  const rest = order.filter(key => !canonicalSet.has(key));
  order = [
    ...CANONICAL_VISIBLE_FILTER_KEYS,
    'all',
    ...rest.filter(key => key !== 'all'),
  ];

  const hiddenSet = new Set(rawHidden);
  CANONICAL_VISIBLE_FILTER_KEYS.forEach(key => hiddenSet.delete(key));
  // Always keep the product default shortcuts pinned visible.
  DEFAULT_VISIBLE_SHORTCUT_KEYS.forEach(key => hiddenSet.delete(key));

  if (shouldMigrateBuiltins) {
    // Prod migration: reset other builtins off; leave folders/customs alone.
    DEFAULT_HIDDEN_BUILTIN_KEYS.forEach(key => hiddenSet.add(key));
  } else {
    DEFAULT_HIDDEN_BUILTIN_KEYS.forEach(key => {
      // Soft default: never-listed keys stay hidden until the user opts in.
      if (!rawOrder.includes(key)) hiddenSet.add(key);
    });
  }

  // Invariant: never show builtin Kanban + kanban-* presets together
  // (v3 migration left both when users had duplicated the chip).
  hideBuiltinKanbanWhenPresetsExist(hiddenSet, kanbanPresets);

  const hidden = [...hiddenSet].filter(
    key => !CANONICAL_VISIBLE_FILTER_KEYS.includes(key)
  );

  // Valid activeKey: keep when still present in order (including after preset delete).
  const candidate = normalizeLegacyPendingKey(
    typeof cfg.activeKey === 'string' ? cfg.activeKey : ''
  );
  const activeKey =
    candidate && order.includes(candidate) ? candidate : 'unread';

  const globalInbox = sanitizeGlobalInboxIds(
    cfg.globalInbox,
    options.validInboxIds
  );

  const builtinDefaultsVersion = shouldMigrateBuiltins
    ? SITEUP_BUILTIN_SHORTCUT_DEFAULTS_VERSION
    : Number(cfg.builtinDefaultsVersion) ||
      SITEUP_BUILTIN_SHORTCUT_DEFAULTS_VERSION;

  return {
    order,
    hidden,
    icons: cfg.icons && typeof cfg.icons === 'object' ? cfg.icons : {},
    showLabels: Boolean(cfg.showLabels),
    compact: Boolean(cfg.compact),
    statuses:
      cfg.statuses && typeof cfg.statuses === 'object' ? cfg.statuses : {},
    globalInbox,
    inboxes: cfg.inboxes && typeof cfg.inboxes === 'object' ? cfg.inboxes : {},
    // Legacy single built-in kanban scope (still honored for key === 'kanban').
    kanbanFunnel: cfg.kanbanFunnel != null ? String(cfg.kanbanFunnel) : '',
    kanbanStage: typeof cfg.kanbanStage === 'string' ? cfg.kanbanStage : '',
    adminAssigneeScope: ADMIN_ASSIGNEE_SCOPE_VALUES.includes(
      cfg.adminAssigneeScope
    )
      ? cfg.adminAssigneeScope
      : ADMIN_ASSIGNEE_SCOPE.ALL,
    includeUnassignedInPendentes: normalizeIncludeUnassignedInPendentes(
      cfg.includeUnassignedInPendentes
    ),
    kanbanPresets,
    activeKey,
    builtinDefaultsVersion,
    needsBuiltinDefaultsPersist: shouldMigrateBuiltins,
  };
};

// Pastas usam rota própria. Ao selecionar um preset padrão, a URL precisa sair
// de custom_view/:id; caso contrário o watcher da pasta reaplica a consulta
// salva e faz o botão parecer quebrado.
export const getQuickFilterNavigationTarget = ({ key, foldersId }) => {
  if (isFolderFilterKey(key)) {
    return {
      name: 'folder_conversations',
      params: { id: folderIdFromKey(key) },
    };
  }

  return foldersId ? { name: 'home' } : null;
};

export const findQuickFilterByKey = (key, kanbanPresets = []) => {
  const builtin = QUICK_FILTERS.find(item => item.key === key);
  if (builtin) return builtin;
  const preset = (kanbanPresets || []).find(item => item.key === key);
  if (!preset) return null;
  return {
    key: preset.key,
    icon: preset.icon || 'i-ph-kanban',
    name: preset.name,
    assigneeType: 'kanban',
    status: STATUS_TYPE.ACTIVE,
    conversationType: '',
    countKey: null,
    defaultHidden: false,
    requiresKanban: true,
    funnelId: preset.funnelId || '',
    stageKey: preset.stageKey || '',
    type: 'kanban_preset',
  };
};

/**
 * Resolve assignee_type for request: ordinary agents' "All" is own history
 * (me + status all). Real administrators keep account-wide all.
 */
export const getAssigneeTypeForQuickFilter = ({
  preset,
  isAdministrator = false,
}) => {
  if (!preset) return ASSIGNEE_TYPE.ALL;
  if (preset.key === 'all' && !isAdministrator) {
    return ASSIGNEE_TYPE.ME;
  }
  return preset.assigneeType;
};

export const getCountKeyForQuickFilter = ({
  preset,
  isAdministrator = false,
}) => {
  if (!preset) return null;
  if (preset.key === 'all' && !isAdministrator) return 'mineCount';
  return preset.countKey;
};

/**
 * Badge value for quick-filter chips.
 * - null/undefined → unknown / no reliable source → hide badge (never false zero)
 * - number ≥ 0 → trusted server count (UI may still hide literal 0)
 */
export const resolveQuickFilterBadgeCount = count => {
  if (count === null || count === undefined || count === '') return null;
  const n = Number(count);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
};

/**
 * Aggregate parallel Kanban badge meta results into one map, then the caller
 * does a single assign. Avoids Promise.all races that spread a stale
 * kanbanCounts snapshot and drop sibling keys.
 *
 * @param {Array<{ key: string, count: number|string|null|undefined }|null|undefined>} results
 * @returns {Record<string, number>} only successful finite counts
 */
export const aggregateKanbanBadgeCounts = results => {
  const next = {};
  if (!Array.isArray(results)) return next;

  results.forEach(entry => {
    if (!entry || entry.key == null || entry.key === '') return;
    const resolved = resolveQuickFilterBadgeCount(entry.count);
    if (resolved === null) return;
    next[entry.key] = resolved;
  });
  return next;
};

// Kanban membership is always server-side for pagination; client still refilters
// the shared cache for realtime ADD/UPDATE/reassignment safety.
export const shouldUseKanbanCache = () => false;

/**
 * Participant user ids from list/realtime payloads.
 * Returns `null` when the payload does not carry membership data (unknown).
 * Empty array means known: no human participants.
 */
export const getConversationParticipantUserIds = conversation => {
  if (!conversation || typeof conversation !== 'object') return null;

  if (Array.isArray(conversation.participant_user_ids)) {
    return conversation.participant_user_ids.map(Number).filter(Boolean);
  }
  if (Array.isArray(conversation.meta?.participant_user_ids)) {
    return conversation.meta.participant_user_ids.map(Number).filter(Boolean);
  }
  if (Array.isArray(conversation.participants)) {
    return conversation.participants
      .map(item => Number(item?.id ?? item?.user_id))
      .filter(Boolean);
  }
  return null;
};

/**
 * Mine = primary assignee is current user OR current user is a participant.
 * Fail-closed for the participant path when ids are unknown: only assignee matches.
 */
export const conversationMatchesMine = (conversation, currentUserId) => {
  const userId = Number(currentUserId);
  if (!userId) return false;

  const assigneeId = Number(conversation?.meta?.assignee?.id);
  if (assigneeId && assigneeId === userId) return true;

  const participantIds = getConversationParticipantUserIds(conversation);
  if (participantIds === null) return false;
  return participantIds.includes(userId);
};

/**
 * Admin "Visão apenas minha" — mirrors ConversationFinder#filter_by_admin_assignee_scope
 * so realtime/cache refilters cannot leave other agents' cards visible.
 */
export const conversationMatchesAdminAssigneeScope = (
  conversation,
  currentUserId,
  adminAssigneeScope,
  { isAdministrator = false } = {}
) => {
  if (!isAdministrator) return true;
  if (adminAssigneeScope !== ADMIN_ASSIGNEE_SCOPE.MINE_AND_UNASSIGNED) {
    return true;
  }

  const userId = Number(currentUserId);
  if (!userId) return false;

  const assigneeId = Number(conversation?.meta?.assignee?.id);
  if (!assigneeId) return true;
  if (assigneeId === userId) return true;

  const participantIds = getConversationParticipantUserIds(conversation);
  if (participantIds !== null && participantIds.includes(userId)) return true;

  return false;
};

/**
 * Unassigned = no human owner: no assignee AND no participants.
 * Fail-closed when participant membership is unknown (exclude from list).
 */
export const conversationMatchesUnassigned = conversation => {
  if (conversation?.meta?.assignee) return false;

  const participantIds = getConversationParticipantUserIds(conversation);
  if (participantIds === null) return false;
  return participantIds.length === 0;
};

/**
 * Kanban membership for the active preset funnel/stage.
 * Fail-closed when kanban_items is missing or empty (no card / other funnel).
 */
export const conversationMatchesKanban = (
  conversation,
  { funnelId = '', stageKey = '' } = {}
) => {
  const items = conversation?.kanban_items;
  if (!Array.isArray(items) || items.length === 0) return false;

  const funnel =
    funnelId != null && String(funnelId).trim() !== '' ? String(funnelId) : '';
  const stage =
    typeof stageKey === 'string' && stageKey.trim() !== '' ? stageKey : '';

  return items.some(item => {
    const itemFunnel = item?.funnel_id != null ? String(item.funnel_id) : '';
    const itemStage =
      typeof item?.funnel_stage === 'string' ? item.funnel_stage : '';
    if (funnel && itemFunnel !== funnel) return false;
    if (stage && itemStage !== stage) return false;
    return true;
  });
};

/**
 * Client-side membership aligned with ConversationFinder assignee_type.
 * Used by list getters and ChatList so realtime cannot leave foreign cards visible.
 */
export const conversationMatchesAssigneeFilter = (
  conversation,
  { assigneeType, currentUserId, kanbanFunnelId = '', kanbanStage = '' } = {}
) => {
  switch (assigneeType) {
    case 'me':
      return conversationMatchesMine(conversation, currentUserId);
    case 'unassigned':
      return conversationMatchesUnassigned(conversation);
    case 'kanban':
      return conversationMatchesKanban(conversation, {
        funnelId: kanbanFunnelId,
        stageKey: kanbanStage,
      });
    default:
      return true;
  }
};

export const getConversationTypeForRequest = ({ conversationType }) =>
  conversationType;

// SiteUp: pesquisa da lista respeita o recorte de inbox ativo (rota ou quick
// filter). Antes, com `q`, limpávamos o scope pessoal e buscávamos em todas as
// caixas — isso misturava resultados fora do filtro da tela.
export const getInboxIdForRequest = ({
  conversationInbox,
  globalInbox,
  validInboxIds,
}) => {
  if (conversationInbox) return conversationInbox;

  const inboxIds = sanitizeGlobalInboxIds(globalInbox, validInboxIds);
  return inboxIds.length ? inboxIds : undefined;
};

export const clearConversationQuickFilterScope = config => ({
  ...(config || {}),
  globalInbox: [],
  kanbanFunnel: '',
  kanbanStage: '',
  adminAssigneeScope: ADMIN_ASSIGNEE_SCOPE.ALL,
  // Per-preset funnel/stage on kanbanPresets is intentional user data — keep it.
});

// SiteUp: indicator chips for active conversation scope (Option B).
// Keep each active delimiter as its own compact chip — never concatenate.
export const MAX_VISIBLE_CONVERSATION_SCOPE_CHIPS = 2;

// Literal icon class names so Tailwind/Uno scans them from this JS module.
export const CONVERSATION_SCOPE_CHIP_ICONS = {
  search: 'i-ri-search-line',
  inbox: 'i-ph-tray',
  groups_hidden: 'i-ph-users',
  groups_only: 'i-ph-users',
  social_hidden: 'i-ri-share-2-line',
  social_only: 'i-ri-share-2-line',
  campaigns_hidden: 'i-ri-rocket-line',
  campaigns_only: 'i-ri-rocket-line',
  my_queue: 'i-ph-user',
  label: 'i-ph-tag',
  label_count: 'i-ph-tag',
  kanban_funnel: 'i-ph-kanban',
  kanban_stage: 'i-ph-kanban',
};

const scopeChip = ({ key, shortLabel, fullLabel, clearable = true }) => ({
  key,
  shortLabel,
  fullLabel,
  icon: CONVERSATION_SCOPE_CHIP_ICONS[key] || 'i-ph-funnel',
  clearable,
});

/**
 * Build discrete scope indicator chips from the active list delimiters.
 * Presentation only — does not mutate filter state.
 *
 * @param {object} params
 * @param {(key: string, values?: object) => string} params.t
 * @returns {Array<{ key: string, shortLabel: string, fullLabel: string, icon: string, clearable: boolean }>}
 */
export const buildConversationScopeChips = ({
  t,
  searchAllInboxes = false,
  hasGlobalInboxScope = false,
  inboxIds = [],
  inboxName = '',
  groupFilterMode = 'all',
  socialFilterMode = 'all',
  campaignFilterMode = 'all',
  isAdministrator = false,
  adminAssigneeScope = ADMIN_ASSIGNEE_SCOPE.ALL,
  labels = [],
  isKanbanTab = false,
  kanbanFunnelId = '',
  kanbanFunnelName = '',
  kanbanStageKey = '',
  kanbanStageName = '',
} = {}) => {
  if (searchAllInboxes) {
    const label = t('CHAT_LIST.FILTER_SCOPE.SEARCH_ALL_INBOXES');
    return [
      scopeChip({
        key: 'search',
        shortLabel: label,
        fullLabel: label,
        clearable: false,
      }),
    ];
  }

  const chips = [];

  if (hasGlobalInboxScope) {
    const ids = (Array.isArray(inboxIds) ? inboxIds : [])
      .map(Number)
      .filter(Boolean);
    if (ids.length === 1) {
      const name = inboxName || t('CHAT_LIST.QUICK_FILTERS.GLOBAL_INBOX.ONE');
      chips.push(
        scopeChip({
          key: 'inbox',
          shortLabel: t('CHAT_LIST.FILTER_SCOPE.SHORT.INBOX', { name }),
          fullLabel: t('CHAT_LIST.FILTER_SCOPE.INBOX', { name }),
        })
      );
    } else if (ids.length > 1) {
      chips.push(
        scopeChip({
          key: 'inbox',
          shortLabel: t('CHAT_LIST.FILTER_SCOPE.SHORT.INBOX_COUNT', {
            count: ids.length,
          }),
          fullLabel: t('CHAT_LIST.FILTER_SCOPE.INBOX_COUNT', {
            count: ids.length,
          }),
        })
      );
    }
  }

  if (groupFilterMode === 'hide') {
    chips.push(
      scopeChip({
        key: 'groups_hidden',
        shortLabel: t('CHAT_LIST.FILTER_SCOPE.SHORT.GROUPS_HIDDEN'),
        fullLabel: t('CHAT_LIST.FILTER_SCOPE.GROUPS_HIDDEN'),
      })
    );
  } else if (groupFilterMode === 'only') {
    chips.push(
      scopeChip({
        key: 'groups_only',
        shortLabel: t('CHAT_LIST.FILTER_SCOPE.SHORT.GROUPS_ONLY'),
        fullLabel: t('CHAT_LIST.FILTER_SCOPE.GROUPS_ONLY'),
      })
    );
  }

  if (socialFilterMode === 'hide') {
    chips.push(
      scopeChip({
        key: 'social_hidden',
        shortLabel: t('CHAT_LIST.FILTER_SCOPE.SHORT.SOCIAL_HIDDEN'),
        fullLabel: t('CHAT_LIST.FILTER_SCOPE.SOCIAL_HIDDEN'),
      })
    );
  } else if (socialFilterMode === 'only') {
    chips.push(
      scopeChip({
        key: 'social_only',
        shortLabel: t('CHAT_LIST.FILTER_SCOPE.SHORT.SOCIAL_ONLY'),
        fullLabel: t('CHAT_LIST.FILTER_SCOPE.SOCIAL_ONLY'),
      })
    );
  }

  if (campaignFilterMode === 'hide') {
    chips.push(
      scopeChip({
        key: 'campaigns_hidden',
        shortLabel: t('CHAT_LIST.FILTER_SCOPE.SHORT.CAMPAIGNS_HIDDEN'),
        fullLabel: t('CHAT_LIST.FILTER_SCOPE.CAMPAIGNS_HIDDEN'),
      })
    );
  } else if (campaignFilterMode === 'only') {
    chips.push(
      scopeChip({
        key: 'campaigns_only',
        shortLabel: t('CHAT_LIST.FILTER_SCOPE.SHORT.CAMPAIGNS_ONLY'),
        fullLabel: t('CHAT_LIST.FILTER_SCOPE.CAMPAIGNS_ONLY'),
      })
    );
  }

  if (
    isAdministrator &&
    adminAssigneeScope === ADMIN_ASSIGNEE_SCOPE.MINE_AND_UNASSIGNED
  ) {
    chips.push(
      scopeChip({
        key: 'my_queue',
        shortLabel: t('CHAT_LIST.FILTER_SCOPE.SHORT.MY_QUEUE'),
        fullLabel: t('CHAT_LIST.FILTER_SCOPE.MY_QUEUE'),
      })
    );
  }

  const labelTitles = Array.isArray(labels) ? labels.filter(Boolean) : [];
  if (labelTitles.length === 1) {
    chips.push(
      scopeChip({
        key: 'label',
        shortLabel: t('CHAT_LIST.FILTER_SCOPE.SHORT.LABEL', {
          name: labelTitles[0],
        }),
        fullLabel: t('CHAT_LIST.FILTER_SCOPE.LABEL', {
          name: labelTitles[0],
        }),
      })
    );
  } else if (labelTitles.length > 1) {
    chips.push(
      scopeChip({
        key: 'label_count',
        shortLabel: t('CHAT_LIST.FILTER_SCOPE.SHORT.LABEL_COUNT', {
          count: labelTitles.length,
        }),
        fullLabel: t('CHAT_LIST.FILTER_SCOPE.LABEL_COUNT', {
          count: labelTitles.length,
        }),
      })
    );
  }

  if (isKanbanTab) {
    if (kanbanFunnelId) {
      const name = kanbanFunnelName || kanbanFunnelId;
      chips.push(
        scopeChip({
          key: 'kanban_funnel',
          shortLabel: t('CHAT_LIST.FILTER_SCOPE.SHORT.KANBAN_FUNNEL', {
            name,
          }),
          fullLabel: t('CHAT_LIST.FILTER_SCOPE.KANBAN_FUNNEL', { name }),
        })
      );
    }
    if (kanbanStageKey) {
      const name = kanbanStageName || kanbanStageKey;
      chips.push(
        scopeChip({
          key: 'kanban_stage',
          shortLabel: t('CHAT_LIST.FILTER_SCOPE.SHORT.KANBAN_STAGE', {
            name,
          }),
          fullLabel: t('CHAT_LIST.FILTER_SCOPE.KANBAN_STAGE', { name }),
        })
      );
    }
  }

  return chips;
};

/**
 * Keep at most `maxVisible` chips in the rail; surplus becomes a +N overflow.
 *
 * @param {Array<{ shortLabel?: string, fullLabel?: string }>} chips
 * @param {number} maxVisible
 */
export const partitionConversationScopeChips = (
  chips,
  maxVisible = MAX_VISIBLE_CONVERSATION_SCOPE_CHIPS
) => {
  const list = Array.isArray(chips) ? chips : [];
  const limit = Number.isFinite(maxVisible) && maxVisible > 0 ? maxVisible : 2;

  if (list.length <= limit) {
    return {
      visible: list,
      overflow: [],
      overflowCount: 0,
      overflowTooltip: '',
    };
  }

  const visible = list.slice(0, limit);
  const overflow = list.slice(limit);
  return {
    visible,
    overflow,
    overflowCount: overflow.length,
    overflowTooltip: overflow
      .map(chip => chip.fullLabel || chip.shortLabel || '')
      .filter(Boolean)
      .join(' · '),
  };
};

/**
 * Recorte que a LISTA aplica além do chip e que o badge tem de herdar.
 *
 * Em `ChatList#conversationFilters` o `conversationType` da ROTA
 * (/conversations/unread, /unattended, /mention, /participating) vence o do
 * chip. Os contadores montavam os presets só com o chip, então numa dessas
 * rotas todo badge contava um universo maior que o da lista (medido em prod na
 * conta 11: chip Minhas com badge 131 e lista de 5, que era minhas ∩ não lidas).
 *
 * @param {{ routeConversationType?: string, presetConversationType?: string }} args
 * @returns {string} '' quando não há recorte.
 */
export const resolveCountsConversationType = ({
  routeConversationType,
  presetConversationType,
} = {}) => routeConversationType || presetConversationType || '';

/**
 * Etiquetas do recorte para os contadores: mesma precedência da lista
 * (rota > filtro rápido do cabeçalho). Antes só a etiqueta de rota entrava,
 * então filtrar por etiqueta encolhia a lista sem mexer no badge.
 *
 * @param {{ routeLabel?: string, quickFilterLabels?: string[] }} args
 * @returns {string[]|undefined} undefined = sem filtro de etiqueta.
 */
export const resolveCountsLabels = ({ routeLabel, quickFilterLabels } = {}) => {
  if (routeLabel) return [routeLabel];
  const labels = Array.isArray(quickFilterLabels) ? quickFilterLabels : [];
  return labels.length ? labels : undefined;
};
